import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import PageLayout from "../../component/PageLayout";
import Pagination from "../../component/Pagination";
import { useFilterPagination } from "../../component/Pagination/useFilterPagination";
import SearchBar from "../../component/SearchFilter";
import IconEdit from "../../component/SvgIcon/IconEdit";
import IconPlus from "../../component/SvgIcon/IconPlus";
import IconUserActive from "../../component/SvgIcon/IconUserActive";
import IconUserDeactive from "../../component/SvgIcon/IconUserDeactive";
import IconUsers from "../../component/SvgIcon/IconUsers";
import { useAuth } from "../../services/AuthContext";
import { BootstrapTooltips } from "../../services/BootstrapTooltips";
import { useSwal } from "../../services/SweetAlert";
import api from "../../services/index";
import "./style.css";
import IconShorting from "../../component/SvgIcon/IconShorting";
import { useNavigate } from "react-router-dom";
import IconLocation from "../../component/SvgIcon/IconLocation";
import IconLocationOffLine from "../../component/SvgIcon/IconLocationOffLine";

const TableColumns = [
  { tableid: "id", header: "#" },
  { tableid: "firstName", header: "Name" },
  { tableid: "email", header: "Email" },
  { tableid: "mobile", header: "Mobile" },
  { tableid: "location", header: "Location" },
  { tableid: "country", header: "Country" },
  { tableid: "usertype", header: "User Type" },
  { tableid: "action", header: "Action" },
];

export default function UserList() {
  const navigate = useNavigate();
  const alert = useSwal();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

  const { userType: UserType, country: Country } = useAuth(); // ✅ define them here

  // console.log("userType:", UserType, "country:", Country);

  const {
    searchQuery,
    setSearchQuery,
    clearSearch,
    page,
    setPage,
    totalPages,
    paginatedData,
    sortConfig,
    setSortConfig,
    pageSize,
  } = useFilterPagination(tableData, {
    defaultPageSize: 10,
    responsivePageSize: 15,
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.post("/adminuserdetils", {
        UserType,
        Country,
      });
      console.log(`API response:${Country}:`, response.data);
      const usersData = (response.data.data || []).map((user) => ({
        id: user.userId ?? "",
        firstName: user.name ?? "",
        email: user.email ?? "",
        mobile: user.mobile ?? "",
        location: user.location ?? "",
        country: user.country ?? "",
        usertype: user.userType ?? "",
        status: user.userStatus ? user.userStatus.toLowerCase() : "inactive",
        is_tracking_on: user.is_tracking_on ?? 0,
      }));
      setTableData(usersData.reverse());
    } catch (error) {
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [UserType, Country]);

  const handleTrackingToggle = async (userId, newStatus) => {
    const actionText = newStatus ? "enable" : "disable";

    const result = await alert(
      "question",
      `Are you sure you want to ${actionText} tracking for this user?`,
    );

    if (result.isConfirmed) {
      setLoading(true);

      try {
        const payload = {
          userId,
          is_tracking_on: newStatus,
        };

        const res = await api.post("/web-update-user-tracking", payload);
        console.log("Full API Response:", res.data);
        console.log("Status Code:", res.status);

        const message = res.data?.message || "";

        if (res.status === 200) {
          alert("success", "User tracking updated successfully!");

          await fetchUsers(); // 👈 CALL AGAIN

          return;
        } else {
          alert("error", message || "Failed to set user tracking.");
        }
      } catch (err) {
        if (err.response) {
          console.error("API Error Response:", err.response.data);
          const apiMessage =
            err.response.data?.message ||
            "An error occurred while submitting the form.";

          alert("error", apiMessage);
        } else {
          console.error("Submit error:", err);
          alert("error", "An error occurred while submitting the form.");
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleStatusToggle = async (userId, newStatus) => {
    const actionText = newStatus === "active" ? "Activate" : "Deactivate";

    const result = await alert(
      "question",
      `Are you sure you want to ${actionText} this user?`,
    );

    if (result.isConfirmed) {
      setLoading(true);

      try {
        const payload = {
          userId,
          status: newStatus,
        };

        const res = await api.post("/web-update-user-status", payload);
        console.log("Full API Response:", res.data);

        const message = res.data?.message || "";

        if (message.includes("User status updated successfully")) {
          alert("success", "User status updated successfully!");

          setTableData((prevData) =>
            prevData.map((user) =>
              user.id === userId ? { ...user, status: newStatus } : user,
            ),
          );

          navigate("/user-list");
        } else {
          alert("error", message || "Failed to set user status.");
        }
      } catch (err) {
        if (err.response) {
          console.error("API Error Response:", err.response.data);
          const apiMessage =
            err.response.data?.message ||
            "An error occurred while submitting the form.";

          alert("error", apiMessage);
        } else {
          console.error("Submit error:", err);
          alert("error", "An error occurred while submitting the form.");
        }
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    BootstrapTooltips();
  }, [paginatedData, sortConfig, searchQuery, page]);

  // Sort paginated data
  const sortedData = [...paginatedData].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const key = sortConfig.key;
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    const valA = (a[key] ?? "").toString().toLowerCase();
    const valB = (b[key] ?? "").toString().toLowerCase();
    return valA > valB ? dir : valA < valB ? -dir : 0;
  });

  return (
    <PageLayout icon={<IconUsers />} pagetitle="User List">
      <div className="topspace">
        <div className="container-fluid">
          <section className="user-section mb-4">
            <div className="filterarea d-flex flex-column gap-3 flex-md-row justify-content-between align-items-center">
              <Link to="/add-user" className="btn addbtn gap-2">
                <IconPlus /> Add User
              </Link>

              <div className="datafilter d-flex gap-2 align-items-center">
                <SearchBar
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  onClear={clearSearch}
                />
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </div>
            </div>

            <div className="table-responsive">
              {loading ? (
                <div className="text-center my-4">Loading users...</div>
              ) : (
                <table className="table">
                  <thead className="table-light">
                    <tr>
                      {TableColumns.map(({ tableid, header }) => (
                        <th
                          key={tableid}
                          className={
                            tableid === "action"
                              ? "action"
                              : tableid === "id" ||
                                  tableid === "country" ||
                                  tableid === "location"
                                ? ""
                                : "cursor-pointer"
                          }
                          style={
                            tableid === "action"
                              ? { width: "8%", whiteSpace: "nowrap" }
                              : {}
                          }
                          onClick={() => {
                            // Only allow sorting for columns other than id and email
                            if (
                              tableid === "action" ||
                              tableid === "id" ||
                              tableid === "country" ||
                              tableid === "location"
                            )
                              return;
                            setSortConfig((prev) => ({
                              key: tableid,
                              direction:
                                prev.key === tableid && prev.direction === "asc"
                                  ? "desc"
                                  : "asc",
                            }));
                          }}
                        >
                          {header}{" "}
                          {["action", "id", "country", "location"].includes(
                            tableid,
                          ) ? null : (
                            <IconShorting
                              activeDirection={
                                sortConfig.key === tableid
                                  ? sortConfig.direction
                                  : "desc"
                              }
                            />
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedData.length > 0 ? (
                      // sortedData.map((row) => (
                      //   <tr key={row.id}>
                      //     <td>{(page - 1) * 10 + sortedData.indexOf(row) + 1}</td>
                      sortedData.map((row, index) => (
                        <tr key={row.id}>
                          <td>{(page - 1) * pageSize + index + 1}</td>
                          <td>{row.firstName}</td>
                          <td>{row.email}</td>
                          <td>{row.mobile}</td>
                          <td>{row.location}</td>
                          <td width={130} className="text-left">
                            <div className="d-flex align-items-center gap-2">
                              {row.country === "India" ? (
                                <>
                                  <img
                                    src="../images/flags/india.png"
                                    alt="India"
                                    width={24}
                                    height={16}
                                  />
                                  <span>India</span>
                                </>
                              ) : row.country === "UAE" ||
                                row.country === "United Arab Emirate" ? (
                                <>
                                  <img
                                    src="../images/flags/united-arab-emirates.png"
                                    alt="UAE"
                                    width={24}
                                    height={16}
                                  />
                                  <span>UAE</span>
                                </>
                              ) : (
                                <>
                                  Inactive
                                  <img
                                    src="../images/flags/tanzania.png"
                                    alt="Tanzania"
                                    width={24}
                                    height={16}
                                  />
                                  <span>Tanzania</span>
                                </>
                              )}
                            </div>
                          </td>
                          <td>{row.usertype}</td>

                          <td className="action">
                            <div className="d-flex gap-2 flex-wrap justify-content-between">
                              {row.is_tracking_on === 1 ? (
                                <button
                                  className="btn-sm btn-danger"
                                  data-bs-toggle="tooltip"
                                  title="Tracking is ON"
                                  onClick={() =>
                                    handleTrackingToggle(row.id, false)
                                  }
                                >
                                  <IconLocationOffLine />
                                </button>
                              ) : (
                                <button
                                  className="btn-sm btn-secondary"
                                  data-bs-toggle="tooltip"
                                  title="Tracking is OFF"
                                  onClick={() =>
                                    handleTrackingToggle(row.id, true)
                                  }
                                >
                                  <IconLocation />
                                </button>
                              )}

                              <Link
                                to={`/edit-user/${row.id}`}
                                className="btn-sm d-flex align-items-center justify-content-center"
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title="Edit User"
                              >
                                <IconEdit />
                              </Link>
                              {row.status === "active" ? (
                                <button
                                  className="btn-sm btn-success"
                                  data-bs-toggle="tooltip"
                                  title="Deactivate User"
                                  onClick={() =>
                                    handleStatusToggle(row.id, "inactive")
                                  }
                                >
                                  <IconUserActive />
                                </button>
                              ) : (
                                <button
                                  className="btn-sm btn-secondary"
                                  data-bs-toggle="tooltip"
                                  title="Activate User"
                                  onClick={() =>
                                    handleStatusToggle(row.id, "active")
                                  }
                                >
                                  <IconUserDeactive />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={TableColumns.length}
                          className="text-center"
                        >
                          No records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </div>
      </div>
    </PageLayout>
  );
}
